/**
 * GIS CSV Upload Service
 * Optimized two-phase approach for large-scale projects
 */

import { FormGroup, FieldTemplate, FieldInstance, FieldInstanceForResolver } from "../../db/definitions";
import { handleBatchCrud } from "../../utils/handleBatchCrud";
import batchCreateFieldInstances from "../../utils/batchCreateFieldInstances";
import { recordSentry } from "../../utils/recordSentry";
import { createFieldForBatchResolver } from "../../db/queries/fieldInstanceQueries";
import { ProjectData, CsvData, ProcessingStats, RowProcessingResult, UpdateSummary, TableRowData, ChangePreview } from "./types";
import { normalizeKey, chooseIdentityCsvKey, getApplicableFormGroupIds, buildRowValueMap } from "./helpers";

// ============================
// Precomputed Maps (Phase 1 Output)
// ============================

interface PrecomputedMaps {
	// Basic ID lookups
	fieldTemplateById: Map<string, FieldTemplate>;

	// CSV validation: csvKey (normalized)
	csvKeySet: Set<string>;

	// Fan-out template matching: formGroupId -> csvKey -> FieldTemplate[]
	templatesByGroupAndCsvKey: Map<string, Map<string, FieldTemplate[]>>;

	// Identity discovery: formGroupId -> FieldTemplateId[]
	identityTemplateIdsByGroup: Map<string, string[]>;
	identityTemplateIdSetByGroup: Map<string, Set<string>>;

	// Identity matching: formGroupId -> normalizedIdentity -> FormGroupId[]
	identityToFormGroupsByIdentity: Map<string, Map<string, string[]>>;

	// Deterministic identity reading: formGroupId -> csvKey
	identityCsvKeyByGroup: Map<string, string>;

	// Fast field instance updates: formGroupId -> fieldTemplateId -> FieldInstance[]
	fieldInstanceByGroupAndTemplate: Map<string, Map<string, FieldInstanceForResolver[]>>;

	// AltInstance lookup: altInstanceId -> FieldInstance
	altInstanceById: Map<string, FieldInstanceForResolver>;

	// Table processing maps
	tableTemplatesByGroup: Map<string, FieldTemplate[]>; // formGroupId -> table parent templates
	tableInstancesByTemplate: Map<string, FieldInstanceForResolver>; // tableTemplateId -> parent table FieldInstance
	tableChildTemplatesByParent: Map<string, FieldTemplate[]>; // parentTableTemplateId -> child templates (ordered)

	// Performance optimization: Pre-filtered table parents for fast lookups
	tableParentsByGroupAndTemplate: Map<string, Map<string, FieldInstanceForResolver[]>>; // formGroupId -> templateId -> parent instances
}

// ============================
// Phase 1: Build Precomputed Maps
// ============================

export function buildPrecomputedMaps(projectData: ProjectData, csvHeaders: string[]): PrecomputedMaps {
	// Count altInstances (fieldInstances without fieldTemplateId)
	const altInstanceCount = projectData.fieldInstances.filter((fi) => !fi.fieldTemplateId).length;

	// Show unique form groups in fieldInstances
	const formGroupsInData = new Set(projectData.fieldInstances.map((fi) => fi.formGroup));

	const maps: PrecomputedMaps = {
		fieldTemplateById: new Map(),
		csvKeySet: new Set(),
		templatesByGroupAndCsvKey: new Map(),
		identityTemplateIdsByGroup: new Map(),
		identityTemplateIdSetByGroup: new Map(),
		identityToFormGroupsByIdentity: new Map(),
		identityCsvKeyByGroup: new Map(),
		fieldInstanceByGroupAndTemplate: new Map(),
		altInstanceById: new Map(),
		tableTemplatesByGroup: new Map(),
		tableInstancesByTemplate: new Map(),
		tableChildTemplatesByParent: new Map(),
		tableParentsByGroupAndTemplate: new Map(),
	};

	// 1. Basic ID lookups
	for (const template of projectData.fieldTemplates) {
		maps.fieldTemplateById.set(template.id, template);
	}

	// 2. CSV header set
	for (const header of csvHeaders) {
		maps.csvKeySet.add(normalizeKey(header));
	}

	// 3. Fan-out template map
	for (const template of projectData.fieldTemplates) {
		if (!template.csvKey || !maps.csvKeySet.has(normalizeKey(template.csvKey))) continue;

		const applicableGroupIds = getApplicableFormGroupIds(template, projectData.fieldInstances);

		for (const groupId of applicableGroupIds) {
			if (!maps.templatesByGroupAndCsvKey.has(groupId)) {
				maps.templatesByGroupAndCsvKey.set(groupId, new Map());
			}

			const csvKey = normalizeKey(template.csvKey);
			const groupMap = maps.templatesByGroupAndCsvKey.get(groupId)!;

			if (!groupMap.has(csvKey)) {
				groupMap.set(csvKey, []);
			}
			groupMap.get(csvKey)!.push(template);
		}
	}

	// 4. Identity template collection
	for (const template of projectData.fieldTemplates) {
		if (!template.identityRole || !template.csvKey || !maps.csvKeySet.has(normalizeKey(template.csvKey))) continue;

		const applicableGroupIds = getApplicableFormGroupIds(template, projectData.fieldInstances);

		for (const groupId of applicableGroupIds) {
			if (!maps.identityTemplateIdsByGroup.has(groupId)) {
				maps.identityTemplateIdsByGroup.set(groupId, []);
			}
			maps.identityTemplateIdsByGroup.get(groupId)!.push(template.id);
		}
	}

	// 5. Identity template membership sets
	for (const [groupId, templateIds] of maps.identityTemplateIdsByGroup) {
		maps.identityTemplateIdSetByGroup.set(groupId, new Set(templateIds));
	}

	// 6. AltInstance mapping by ID (MOVED UP - must be before identity mapping)
	for (const fieldInstance of projectData.fieldInstances) {
		// altInstances don't have fieldTemplateId
		if (!fieldInstance.fieldTemplateId) {
			maps.altInstanceById.set(fieldInstance.id, fieldInstance);
		}
	}

	// 7. Identity match map (from existing data)
	console.log("=== Building Identity Mappings ===");
	for (const fieldInstance of projectData.fieldInstances) {
		const formGroupId = fieldInstance.formGroup;
		const identitySet = maps.identityTemplateIdSetByGroup.get(formGroupId);

		const fieldTemplateId = fieldInstance.fieldTemplateId;
		if (!fieldTemplateId || !identitySet?.has(fieldTemplateId)) continue;

		// Get the field template to check identity role
		const template = maps.fieldTemplateById.get(fieldTemplateId);
		const identityRole = template?.identityRole;

		// Get the actual identity value - check altInstance first, then field instance
		let identityValue: string;
		if (fieldInstance.altInstanceId) {
			const altInstance = maps.altInstanceById.get(fieldInstance.altInstanceId);
			if (altInstance) {
				identityValue = altInstance?.value || "";
			} else {
				identityValue = fieldInstance.value || "";
			}
		} else {
			identityValue = fieldInstance.value || "";
		}

		const normalizedIdentity = normalizeKey(identityValue);
		if (!normalizedIdentity) continue;

		if (!maps.identityToFormGroupsByIdentity.has(formGroupId)) {
			maps.identityToFormGroupsByIdentity.set(formGroupId, new Map());
		}

		const groupMap = maps.identityToFormGroupsByIdentity.get(formGroupId)!;
		if (!groupMap.has(normalizedIdentity)) {
			groupMap.set(normalizedIdentity, []);
		}

		const formGroupIds = groupMap.get(normalizedIdentity)!;
		if (!formGroupIds.includes(formGroupId)) {
			formGroupIds.push(formGroupId);
		}
	}

	// 7. Identity CSV column selection
	for (const [groupId, templateIds] of maps.identityTemplateIdsByGroup) {
		const csvKey = chooseIdentityCsvKey(templateIds, maps.fieldTemplateById);
		if (csvKey) {
			maps.identityCsvKeyByGroup.set(groupId, csvKey);
		}
	}

	// 8. Field instance mapping by group and template
	for (const fieldInstance of projectData.fieldInstances) {
		const formGroupId = fieldInstance.formGroup;

		if (!maps.fieldInstanceByGroupAndTemplate.has(formGroupId)) {
			maps.fieldInstanceByGroupAndTemplate.set(formGroupId, new Map());
		}

		const templateMap = maps.fieldInstanceByGroupAndTemplate.get(formGroupId)!;

		// altInstances dont have fieldTemplateId so we have to check for that before using it.
		// at this point we dont need to worry about altinstances, because the gis fields are not
		// linked fields yet.. but when they are well have to figure that out (Though an altInstance)
		// (having a field template doesnt make sense because they will be across multiple fields.
		// with different field templates.)
		const fieldTemplateId = fieldInstance.fieldTemplateId ?? null;
		if (fieldTemplateId && !templateMap.has(fieldTemplateId)) {
			templateMap.set(fieldTemplateId, []);
		}

		if (fieldTemplateId) {
			templateMap.get(fieldTemplateId)!.push(fieldInstance);
		}
	}

	// 8.5. Table parent optimization map - filter out table parents for fast lookup
	for (const [formGroupId, templateMap] of maps.fieldInstanceByGroupAndTemplate) {
		if (!maps.tableParentsByGroupAndTemplate.has(formGroupId)) {
			maps.tableParentsByGroupAndTemplate.set(formGroupId, new Map());
		}

		const parentMap = maps.tableParentsByGroupAndTemplate.get(formGroupId)!;

		for (const [templateId, instances] of templateMap) {
			// Filter for table parents (those without tableField property)
			const tableParents = instances.filter((fi) => !fi.tableField);
			if (tableParents.length > 0) {
				parentMap.set(templateId, tableParents);
			}
		}
	}

	// 9. Table processing maps
	// Find table parent templates (inputType === "table")
	for (const template of projectData.fieldTemplates) {
		if (template.inputType === "table") {
			const applicableGroupIds = getApplicableFormGroupIds(template, projectData.fieldInstances);

			for (const groupId of applicableGroupIds) {
				if (!maps.tableTemplatesByGroup.has(groupId)) {
					maps.tableTemplatesByGroup.set(groupId, []);
				}
				maps.tableTemplatesByGroup.get(groupId)!.push(template);
			}

			// Build child template mapping using rowOrder
			if (template.rowOrder && Array.isArray(template.rowOrder)) {
				const childTemplates: FieldTemplate[] = [];
				for (const childTemplateId of template.rowOrder) {
					const childTemplate = maps.fieldTemplateById.get(childTemplateId);
					if (childTemplate) {
						childTemplates.push(childTemplate);
					}
				}
				maps.tableChildTemplatesByParent.set(template.id, childTemplates);
			}
		}
	}

	// 10. Table instance mapping (parent table field instances)
	for (const fieldInstance of projectData.fieldInstances) {
		if (fieldInstance.fieldTemplateId) {
			const template = maps.fieldTemplateById.get(fieldInstance.fieldTemplateId);
			if (template?.inputType === "table") {
				maps.tableInstancesByTemplate.set(fieldInstance.fieldTemplateId, fieldInstance);
			}
		}
	}

	return maps;
}

// ============================
// Phase 2: Process CSV Rows
// ============================

export function resolveFormGroupId(rowMap: Map<string, string>, maps: PrecomputedMaps): string | null {
	// Try each form group and ALL their identity keys to find a match
	for (const [groupId, identityTemplateIds] of maps.identityTemplateIdsByGroup) {
		// Check all identity fields for this form group
		for (const templateId of identityTemplateIds) {
			const template = maps.fieldTemplateById.get(templateId);
			if (!template?.csvKey) continue;

			const identityCsvKey = normalizeKey(template.csvKey);
			const identityValue = rowMap.get(identityCsvKey);

			if (!identityValue) continue;

			const normalizedIdentity = normalizeKey(identityValue);
			if (!normalizedIdentity) continue;

			const identityMap = maps.identityToFormGroupsByIdentity.get(groupId);

			const formGroupIds = identityMap?.get(normalizedIdentity);
			if (formGroupIds && formGroupIds.length > 0) {
				return groupId;
			}
		}
	}

	console.log("  ❌ No form group match found");
	return null;
}

export function processCSVRows(csvData: CsvData, maps: PrecomputedMaps, projectData: ProjectData): ProcessingStats {
	const stats: ProcessingStats = {
		rowsProcessed: 0,
		rowsInserted: 0,
		rowsUpdated: 0,
		rowsNoGroupMatch: 0,
		rowsNoIdentityValue: 0,
		rowsIdentityNotFound: 0,
		ambiguousIdentityMatches: 0,
		totalFieldUpdatesApplied: 0,
		tableRowsCreated: 0,
		documentsForBatch: [],
		updateSummary: {
			fieldInstanceUpdates: 0,
			altInstanceUpdates: 0,
			fieldInstancesUpdated: new Set(),
			altInstancesUpdated: new Set(),
		},
		warnings: [],
	};

	// Process each row independently with clean separation
	const rowResults: RowProcessingResult[] = [];

	for (let rowIndex = 0; rowIndex < csvData.rows.length; rowIndex++) {
		const result = processSingleCSVRow(rowIndex, csvData, maps, projectData);
		rowResults.push(result);
		stats.rowsProcessed++;
	}

	// Combine results with clean separation
	return combineRowResults(rowResults, stats, projectData);
}

function processSingleCSVRow(rowIndex: number, csvData: CsvData, maps: PrecomputedMaps, projectData: ProjectData): RowProcessingResult {
	const csvRow = csvData.rows[rowIndex];
	const result: RowProcessingResult = {
		rowIndex,
		formGroupId: null,
		type: "skipped",
		fieldUpdates: [],
		updateSummary: {
			fieldInstanceUpdates: 0,
			altInstanceUpdates: 0,
			fieldInstancesUpdated: new Set(),
			altInstancesUpdated: new Set(),
		},
	};

	// Build row map
	const rowMap = buildRowValueMap(csvData.headers, csvRow);
	// console.log(`\n=== Processing CSV Row ${rowIndex + 1} ===`);
	// console.log(`Raw CSV row:`, csvRow);
	// console.log(
	// 	`Row map (non-empty values):`,
	// 	Array.from(rowMap.entries()).filter(([k, v]) => v && v.trim()),
	// );

	// Check for identity value - examine ALL identity fields, not just the chosen ones
	let hasIdentityValue = false;
	for (const [groupId, identityTemplateIds] of maps.identityTemplateIdsByGroup) {
		for (const templateId of identityTemplateIds) {
			const template = maps.fieldTemplateById.get(templateId);
			if (!template?.csvKey) continue;

			const identityCsvKey = normalizeKey(template.csvKey);
			const identityValue = rowMap.get(identityCsvKey);
			if (identityValue && identityValue.trim()) {
				hasIdentityValue = true;
				break;
			}
		}
		if (hasIdentityValue) break;
	}

	if (!hasIdentityValue) {
		// Process as additional table row
		const tableResult = processAdditionalTableRowClean(rowIndex, rowMap, csvData, maps, projectData);
		// If this is a skipped additional table row, we need to count it as no identity value
		if (tableResult.type === "skipped" && tableResult.formGroupId === null) {
			// Mark this specially so we can count it as no identity value
			tableResult.formGroupId = "NO_IDENTITY_VALUE";
		}
		return tableResult;
	}

	// Process as main row with identity
	return processMainRowClean(rowIndex, rowMap, maps, projectData);
}

function processMainRowClean(rowIndex: number, rowMap: Map<string, string>, maps: PrecomputedMaps, projectData: ProjectData): RowProcessingResult {
	const result: RowProcessingResult = {
		rowIndex,
		formGroupId: null,
		type: "skipped",
		fieldUpdates: [],
		updateSummary: {
			fieldInstanceUpdates: 0,
			altInstanceUpdates: 0,
			fieldInstancesUpdated: new Set(),
			altInstancesUpdated: new Set(),
		},
	};

	// Resolve form group
	const formGroupId = resolveFormGroupId(rowMap, maps);
	// console.log(`Resolved form group: ${formGroupId}`);
	if (!formGroupId) {
		// No form group found - this row should be counted as no group match
		return result; // type remains "skipped"
	}

	result.formGroupId = formGroupId;

	// Validate identity using any available identity field
	const identityTemplateIds = maps.identityTemplateIdsByGroup.get(formGroupId);
	if (!identityTemplateIds || identityTemplateIds.length === 0) return result;

	// Try each identity template to find a valid identity value
	let validIdentityFound = false;
	for (const templateId of identityTemplateIds) {
		const template = maps.fieldTemplateById.get(templateId);
		if (!template?.csvKey) continue;

		const identityCsvKey = normalizeKey(template.csvKey);
		const identityValue = rowMap.get(identityCsvKey);
		const normalizedIdentity = normalizeKey(identityValue);

		if (!normalizedIdentity) continue;

		const matchedFormGroups = maps.identityToFormGroupsByIdentity.get(formGroupId)?.get(normalizedIdentity);
		if (matchedFormGroups && matchedFormGroups.length > 0) {
			validIdentityFound = true;
			break;
		}
	}

	if (!validIdentityFound) {
		// console.log(`Row ${rowIndex + 1}: Identity not found - SKIPPING`);
		return result; // type remains "skipped"
	}

	// console.log(`Row ${rowIndex + 1}: Identity check PASSED`);

	// CLEAN SEPARATION: Process field updates ONLY
	const updateResult = processFieldUpdatesOnly(rowMap, formGroupId, maps, projectData);
	result.fieldUpdates = updateResult.fieldUpdates;
	result.updateSummary = updateResult.updateSummary;
	// console.log(`Row ${rowIndex + 1}: Created ${result.fieldUpdates.length} field updates`);

	// CLEAN SEPARATION: Collect table row data (don't update parents yet)
	const tableDataResult = collectTableRowData(rowMap, formGroupId, maps, projectData);
	result.tableRowData = tableDataResult.tableRowData;
	// console.log(`Row ${rowIndex + 1}: Collected ${tableDataResult.totalChildren} table children`);

	// Debug: Show total documents created for this row
	const totalDocs = result.fieldUpdates.length + tableDataResult.totalChildren;
	// console.log(`Row ${rowIndex + 1}: Total documents prepared: ${totalDocs}`);

	result.type = "field_updates";
	return result;
}

function processAdditionalTableRowClean(
	rowIndex: number,
	rowMap: Map<string, string>,
	csvData: CsvData,
	maps: PrecomputedMaps,
	projectData: ProjectData,
): RowProcessingResult {
	const result: RowProcessingResult = {
		rowIndex,
		formGroupId: null,
		type: "skipped",
		fieldUpdates: [],
		updateSummary: {
			fieldInstanceUpdates: 0,
			altInstanceUpdates: 0,
			fieldInstancesUpdated: new Set(),
			altInstancesUpdated: new Set(),
		},
	};

	// console.log(`Row ${rowIndex + 1}: No identity value - checking for additional table row`);

	// Find target form group from previous rows
	let targetFormGroup: string | null = null;
	for (let prevIndex = rowIndex - 1; prevIndex >= 0; prevIndex--) {
		const prevRow = csvData.rows[prevIndex];
		const prevRowMap = buildRowValueMap(csvData.headers, prevRow);
		const prevFormGroupId = resolveFormGroupId(prevRowMap, maps);
		if (prevFormGroupId) {
			targetFormGroup = prevFormGroupId;
			break;
		}
	}

	if (!targetFormGroup) {
		// console.log(`Row ${rowIndex + 1}: No previous form group found`);
		// This row has no identity value and can't find a target form group
		// It should be counted as "no identity value"
		return result; // type remains "skipped", formGroupId remains null
	}

	result.formGroupId = targetFormGroup;
	// console.log(`Row ${rowIndex + 1}: Processing as additional table row for ${targetFormGroup}`);

	// CLEAN SEPARATION: Collect additional table row data (don't update parents yet)
	const tableDataResult = collectTableRowData(rowMap, targetFormGroup, maps, projectData);
	result.tableRowData = tableDataResult.tableRowData;
	// console.log(`Row ${rowIndex + 1}: Collected ${tableDataResult.totalChildren} additional table children`);

	result.type = "additional_table";
	return result;
}

function combineRowResults(rowResults: RowProcessingResult[], stats: ProcessingStats, projectData: ProjectData): ProcessingStats {
	// Combine all results with clean separation and table row collection
	const globalTableRowCollector = new Map<string, TableRowData[]>();
	let totalChildrenCollected = 0;

	for (const rowResult of rowResults) {
		// Add field updates to batch
		stats.documentsForBatch.push(...rowResult.fieldUpdates);
		stats.totalFieldUpdatesApplied += rowResult.fieldUpdates.length;

		// Collect table row data from each row (merge into global collector)
		if (rowResult.tableRowData) {
			for (const [parentId, rowDataArray] of rowResult.tableRowData) {
				if (!globalTableRowCollector.has(parentId)) {
					globalTableRowCollector.set(parentId, []);
				}
				globalTableRowCollector.get(parentId)!.push(...rowDataArray);

				// Add children to batch
				for (const rowData of rowDataArray) {
					stats.documentsForBatch.push(...rowData.children);
					totalChildrenCollected += rowData.children.length;
				}
			}
		}

		// Update summary stats
		stats.updateSummary.fieldInstanceUpdates += rowResult.updateSummary.fieldInstanceUpdates;
		stats.updateSummary.altInstanceUpdates += rowResult.updateSummary.altInstanceUpdates;
		rowResult.updateSummary.fieldInstancesUpdated.forEach((id) => stats.updateSummary.fieldInstancesUpdated.add(id));
		rowResult.updateSummary.altInstancesUpdated.forEach((id) => stats.updateSummary.altInstancesUpdated.add(id));

		// Count row types more precisely
		if (rowResult.type === "field_updates") {
			stats.rowsUpdated++;
		} else if (rowResult.type === "additional_table") {
			stats.rowsInserted++;
		} else if (rowResult.type === "skipped") {
			if (rowResult.formGroupId === "NO_IDENTITY_VALUE") {
				stats.rowsNoIdentityValue++;
			} else if (rowResult.formGroupId === null) {
				stats.rowsNoGroupMatch++;
			} else {
				// Identity value was found but no match in existing data
				stats.rowsIdentityNotFound++;
			}
		}
	}

	// Create final parent updates (one per table parent, no duplicates)
	const parentUpdates = createParentUpdatesFromCollection(globalTableRowCollector, projectData);
	stats.documentsForBatch.push(...parentUpdates);

	stats.tableRowsCreated = totalChildrenCollected;

	return stats;
}

function processFieldUpdatesOnly(
	rowMap: Map<string, string>,
	formGroupId: string,
	maps: PrecomputedMaps,
	projectData: ProjectData,
): { fieldUpdates: FieldInstanceForResolver[]; updateSummary: UpdateSummary } {
	const fieldUpdates: FieldInstanceForResolver[] = [];
	const updateSummary: UpdateSummary = {
		fieldInstanceUpdates: 0,
		altInstanceUpdates: 0,
		fieldInstancesUpdated: new Set(),
		altInstancesUpdated: new Set(),
	};

	const groupTemplateMap = maps.templatesByGroupAndCsvKey.get(formGroupId);
	if (!groupTemplateMap) {
		// console.log(`  processFieldUpdatesOnly: No templates found for form group ${formGroupId}`);
		return { fieldUpdates, updateSummary };
	}

	// console.log(`  processFieldUpdatesOnly: Found ${groupTemplateMap.size} CSV keys mapped for form group ${formGroupId}`);
	const currentTime = new Date().toISOString();

	for (const [csvKey, rawValue] of rowMap) {
		const templates = groupTemplateMap.get(csvKey);
		if (!templates || templates.length === 0) {
			// console.log(`    CSV key "${csvKey}" not found in template map`);
			continue;
		}

		// Skip empty values
		if (!rawValue || !rawValue.trim()) {
			continue;
		}

		const parsedValue = rawValue.trim();

		for (const template of templates) {
			// Get field instances for this template in this form group
			const templateFieldMap = maps.fieldInstanceByGroupAndTemplate.get(formGroupId);
			if (!templateFieldMap) {
				continue;
			}

			const fieldInstances = templateFieldMap.get(template.id);
			if (!fieldInstances || fieldInstances.length === 0) {
				continue;
			}

			// Filter out table children - only update non-table field instances
			const nonTableFieldInstances = fieldInstances.filter((fi) => !fi.tableField);

			// Update existing field instances ONLY (excluding table children)
			for (const fieldInstance of nonTableFieldInstances) {
				const altInstance = maps.altInstanceById.get(fieldInstance.id);
				if (altInstance) {
					// Update altInstance with priority - use createFieldForBatchResolver with ALL existing data
					const updatedAltInstance = createFieldForBatchResolver({
						...altInstance, // Pass ALL existing field data
						value: parsedValue, // Override only the value
					});
					fieldUpdates.push(updatedAltInstance);
					updateSummary.altInstanceUpdates++;
					updateSummary.altInstancesUpdated.add(altInstance.id);
				} else {
					// Update primary field instance - use createFieldForBatchResolver with ALL existing data
					const updatedFieldInstance = createFieldForBatchResolver({
						...fieldInstance, // Pass ALL existing field data
						value: parsedValue, // Override only the value
					});
					fieldUpdates.push(updatedFieldInstance);
					updateSummary.fieldInstanceUpdates++;
					updateSummary.fieldInstancesUpdated.add(fieldInstance.id);
				}
			}
		}
	}

	return { fieldUpdates, updateSummary };
}

function collectTableRowData(
	rowMap: Map<string, string>,
	formGroupId: string,
	maps: PrecomputedMaps,
	projectData: ProjectData,
): { tableRowData: Map<string, TableRowData[]>; totalChildren: number } {
	const tableRowData = new Map<string, TableRowData[]>();
	let totalChildren = 0;

	// Get table templates for this form group
	const tableTemplates = maps.tableTemplatesByGroup.get(formGroupId);
	if (!tableTemplates || tableTemplates.length === 0) {
		return { tableRowData, totalChildren };
	}

	// Process each table template
	for (const tableTemplate of tableTemplates) {
		const childTemplates = maps.tableChildTemplatesByParent.get(tableTemplate.id);
		if (!childTemplates || childTemplates.length === 0) continue;

		// Check if this table has data in the CSV row
		const columnData: { template: FieldTemplate; value: string; csvKey: string }[] = [];
		let hasAnyMatch = false;

		for (const childTemplate of childTemplates) {
			const templateCsvKey = normalizeKey(childTemplate.csvKey?.trim() || "");
			if (!templateCsvKey) continue;

			const cellValue = rowMap.get(templateCsvKey);
			if (cellValue && cellValue.trim()) {
				columnData.push({ template: childTemplate, value: cellValue.trim(), csvKey: templateCsvKey });
				hasAnyMatch = true;
			}
		}

		if (!hasAnyMatch) continue;

		// Find ALL existing table parents for this template (optimized O(1) lookup)
		const existingParents = maps.tableParentsByGroupAndTemplate.get(formGroupId)?.get(tableTemplate.id) || [];

		if (existingParents.length === 0) {
			continue;
		}

		// Create rows for ALL table parents with this template
		for (const existingParent of existingParents) {
			// Create child field instances for this row
			const children: FieldInstanceForResolver[] = [];
			for (const { template, value } of columnData) {
				const childDoc = createFieldForBatchResolver({
					fieldTemplateId: template.id,
					formGroup: formGroupId,
					group: existingParent.group,
					value: value,
					tableField: existingParent.id,
				});
				children.push(childDoc);
				totalChildren++;
			}

			// Add to collection
			if (!tableRowData.has(existingParent.id)) {
				tableRowData.set(existingParent.id, []);
			}
			tableRowData.get(existingParent.id)!.push({
				parentId: existingParent.id,
				children,
			});
		}
	}

	return { tableRowData, totalChildren };
}

function createParentUpdatesFromCollection(tableRowCollector: Map<string, TableRowData[]>, projectData: ProjectData): FieldInstanceForResolver[] {
	const parentUpdates: FieldInstanceForResolver[] = [];

	for (const [parentId, rows] of tableRowCollector) {
		// Find original parent
		const originalParent = projectData.fieldInstances.find((fi) => fi.id === parentId);
		if (!originalParent) continue;

		// Collect all child IDs from all rows for this parent
		const allRowsFieldsOrder: string[] = [];
		for (const rowData of rows) {
			const rowChildIds = rowData.children.map((child) => child.id);
			const rowString = rowChildIds.join(",");
			allRowsFieldsOrder.push(rowString);
		}

		// Update parent with complete fieldsOrder (RESET to clean state, don't append to corrupted existing data)
		const finalFieldsOrder = allRowsFieldsOrder; // Use ONLY our clean row data

		const updatedParent = createFieldForBatchResolver({
			...originalParent,
			fieldsOrder: finalFieldsOrder,
			updatedAt: new Date().toISOString(),
		});

		parentUpdates.push(updatedParent);
	}

	return parentUpdates;
}

// ============================
// CSV Upload Integration Functions
// ============================

export async function previewGeoCSVImportOptimized(
	projectId: string,
	csvText: string,
	projectData: ProjectData,
): Promise<{ stats: ProcessingStats; success: boolean }> {
	try {
		// Parse CSV
		const lines = csvText.trim().split("\n");
		if (lines.length < 2) {
			throw new Error("CSV must have at least a header and one data row");
		}

		const csvData: CsvData = {
			headers: lines[0].split(",").map((h) => h.trim()),
			rows: lines.slice(1).map((line) => line.split(",").map((cell) => cell.trim())),
		};

		// Phase 1: Build maps
		const maps = buildPrecomputedMaps(projectData, csvData.headers);

		// Phase 2: Process (dry run for preview)
		const stats = processCSVRows(csvData, maps, projectData);

		return { stats, success: true };
	} catch (error) {
		return {
			stats: {
				rowsProcessed: 0,
				rowsInserted: 0,
				rowsUpdated: 0,
				rowsNoGroupMatch: 0,
				rowsNoIdentityValue: 0,
				rowsIdentityNotFound: 0,
				ambiguousIdentityMatches: 0,
				totalFieldUpdatesApplied: 0,
				tableRowsCreated: 0,
				documentsForBatch: [],
				updateSummary: {
					fieldInstanceUpdates: 0,
					altInstanceUpdates: 0,
					fieldInstancesUpdated: new Set(),
					altInstancesUpdated: new Set(),
				},
				warnings: [error instanceof Error ? error.message : "Unknown error"],
			},
			success: false,
		};
	}
}

// ============================
// Integration with FormGroupList
// ============================

/**
 * Converts FormGroupList data to ProjectData format for optimized processing
 */
export function createProjectDataFromFormGroups(
	projectId: string,
	formGroups: FormGroup[],
	fieldTemplates: FieldTemplate[],
	fieldInstances: FieldInstanceForResolver[],
): ProjectData {
	return {
		projectId,
		formGroups,
		formTemplates: [],
		fieldTemplates,
		fieldInstances,
	};
}

/**
 * Main integration function for FormGroupList
 * Uses the optimized two-phase approach
 */
export async function previewGeoCSVImport(
	projectId: string,
	csvText: string,
	formGroups: FormGroup[],
	fieldTemplates: FieldTemplate[],
	fieldInstances: FieldInstanceForResolver[],
): Promise<ChangePreview> {
	try {
		// Convert to optimized data structure
		const projectData = createProjectDataFromFormGroups(projectId, formGroups, fieldTemplates, fieldInstances);

		// Use optimized preview
		const result = await previewGeoCSVImportOptimized(projectId, csvText, projectData);

		if (!result.success) {
			return {
				success: false,
				stats: result.stats,
				formGroupsAffected: 0,
				fieldUpdates: 0,
				tableRowsCreated: 0,
				warnings: result.stats.warnings,
			};
		}

		// Calculate actual form groups affected by counting unique form groups in documentsForBatch
		const affectedFormGroups = new Set<string>();
		for (const doc of result.stats.documentsForBatch) {
			if (doc.formGroup && doc.formGroup !== "NO_IDENTITY_VALUE") {
				affectedFormGroups.add(doc.formGroup);
			}
		}
		const formGroupsAffected = affectedFormGroups.size;

		return {
			success: true,
			stats: result.stats,
			formGroupsAffected,
			fieldUpdates: result.stats.totalFieldUpdatesApplied,
			tableRowsCreated: result.stats.rowsInserted, // Use rowsInserted for actual CSV table rows
			warnings: result.stats.warnings,
		};
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
		return {
			success: false,
			stats: {
				rowsProcessed: 0,
				rowsInserted: 0,
				rowsUpdated: 0,
				rowsNoGroupMatch: 0,
				rowsNoIdentityValue: 0,
				rowsIdentityNotFound: 0,
				ambiguousIdentityMatches: 0,
				totalFieldUpdatesApplied: 0,
				tableRowsCreated: 0,
				documentsForBatch: [],
				updateSummary: {
					fieldInstanceUpdates: 0,
					altInstanceUpdates: 0,
					fieldInstancesUpdated: new Set(),
					altInstancesUpdated: new Set(),
				},
				warnings: [errorMessage],
			},
			formGroupsAffected: 0,
			fieldUpdates: 0,
			tableRowsCreated: 0,
			warnings: [errorMessage],
		};
	}
}

/**
 * Function to apply CSV import from a ChangePreview object
 * Used by FormGroupList component
 */
export async function applyGeoCSVImportFromPreview(preview: ChangePreview): Promise<{ success: boolean; appliedUpdates: number; errors?: string[] }> {
	try {
		if (!preview.success || !preview.stats.documentsForBatch.length) {
			return {
				success: false,
				appliedUpdates: 0,
				errors: ["No valid changes to apply"],
			};
		}

		// console.log(`Applying ${preview.stats.documentsForBatch.length} document changes from preview...`);

		// Apply using batch operations with proper chunking (PUT handles both creates and updates)
		const batchResult = await handleBatchCrud(preview.stats.documentsForBatch, batchCreateFieldInstances, 25);

		// console.log(`Successfully applied ${preview.stats.documentsForBatch.length} changes from preview`);
		return {
			success: true,
			appliedUpdates: preview.stats.documentsForBatch.length,
		};
	} catch (error) {
		const errorMessage = (error as Error).message || "Unknown error occurred";
		return {
			success: false,
			appliedUpdates: 0,
			errors: [errorMessage],
		};
	}
}
